import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { fetchUnopenedChests, openRewardChest, type RewardChest, type RewardChestOpenResult } from '@/api/rewardChest';
import { useSessionStore } from '@/stores/session.store';

/**
 * Coffres de progression (niveau / rang) NON ouverts du joueur. Le badge du
 * header lit `count` ; la modale lit `chests` et appelle `open`.
 */
export const useRewardChestsStore = defineStore('rewardChests', () => {
  const session = useSessionStore();
  const chests = ref<RewardChest[]>([]);
  const loading = ref(false);

  const count = computed(() => chests.value.length);

  async function refresh(): Promise<void> {
    const pu = session.activePlayerUniverseId;
    if (pu == null) { chests.value = []; return; }
    loading.value = true;
    try {
      chests.value = await fetchUnopenedChests(pu);
    } catch {
      // silencieux : le badge se remettra à jour au prochain refresh.
    } finally {
      loading.value = false;
    }
  }

  // Les coffres sont SCOPÉS au PlayerUniverse actif. Quand le joueur change
  // d'univers (reprise/join d'une autre partie), l'ancien `activePlayerUniverseId`
  // est remplacé : on VIDE immédiatement la liste (sinon les coffres de l'univers
  // précédent restent affichés/comptés -> impression de « niveaux qui transitent »)
  // puis on refetch pour le nouvel univers. Le watch `inUniverse` du header ne
  // couvrait pas ce cas (il reste `true` d'un univers à l'autre).
  watch(() => session.activePlayerUniverseId, (pu, prev) => {
    if (pu === prev) return;
    chests.value = [];
    if (pu != null) void refresh();
  });

  /** Ouvre un coffre ; retire de la liste et renvoie le butin (pour la révélation). */
  async function open(chestId: number): Promise<RewardChestOpenResult | null> {
    const pu = session.activePlayerUniverseId;
    if (pu == null) return null;
    const result = await openRewardChest(chestId, pu);
    chests.value = chests.value.filter((c) => c.id !== chestId);
    return result;
  }

  return { chests, loading, count, refresh, open };
});
